import background1 from "./assets/background1.png";
import { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ChevronDown, 
  Mail, 
  Linkedin, 
  Download, 
  Building2, 
  TrendingUp, 
  Cpu, 
  Shield,
  Users,
  Award,
  ArrowRight,
  CheckCircle,
  Send,
  MessageCircle
} from "lucide-react";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';

const HomePage = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    inquiry_type: 'consulting'
  });
  const [turnstileToken, setTurnstileToken] = useState('');

  useEffect(() => {
    // Smooth scrolling behavior
    document.documentElement.style.scrollBehavior = 'smooth';

    // Expose Turnstile callback to global scope with error handling
    window.onTurnstileCallback = (token) => {
      try {
        console.log('Turnstile success:', token);
        // Accept both real tokens and dummy tokens for test key
        if (token && (token.length > 10 || token.includes('DUMMY'))) {
          setTurnstileToken(token);
        }
      } catch (error) {
        console.error('Error in Turnstile callback:', error);
      }
    };

    window.onTurnstileExpired = () => {
      try {
        console.log('Turnstile expired');
        setTurnstileToken('');
      } catch (error) {
        console.error('Error in Turnstile expired callback:', error);
      }
    };

    window.onTurnstileError = (errorCode) => {
      try {
        console.log('Turnstile error:', errorCode);
        setTurnstileToken('');
        // Don't show alert for test environment
        if (process.env.NODE_ENV === 'production') {
          alert('Security verification failed. Please try again.');
        }
      } catch (error) {
        console.error('Error in Turnstile error callback:', error);
      }
    };

    // Function to render Turnstile widget explicitly with better error handling
    const renderTurnstile = () => {
      try {
        const container = document.querySelector('.cf-turnstile');
        if (window.turnstile && container && !container.innerHTML) {
          console.log('Attempting to render Turnstile widget...');
          
          const widgetId = window.turnstile.render(container, {
            sitekey: TURNSTILE_SITE_KEY,
            callback: window.onTurnstileCallback,
            'expired-callback': window.onTurnstileExpired,
            'error-callback': window.onTurnstileError,
            theme: 'light',
            size: 'normal'
          });
          
          console.log('Turnstile widget rendered successfully with ID:', widgetId);
        } else if (window.turnstile && container && container.innerHTML) {
          console.log('Turnstile widget already rendered');
        } else if (window.turnstile) {
          console.log('Turnstile script loaded but container not ready, retrying...');
          setTimeout(renderTurnstile, 500);
        } else {
          console.log('Turnstile script not loaded yet, retrying...');
          setTimeout(renderTurnstile, 1000);
        }
      } catch (error) {
        console.error('Error rendering Turnstile widget:', error);
        // Fallback: hide the security verification section if Turnstile fails
        const container = document.querySelector('.cf-turnstile');
        if (container && container.parentElement) {
          container.parentElement.style.display = 'none';
        }
      }
    };

    // Start attempting to render after a short delay to ensure component is mounted
    const renderTimeout = setTimeout(renderTurnstile, 1000);

    // Cleanup
    return () => {
      try {
        delete window.onTurnstileCallback;
        delete window.onTurnstileExpired;
        delete window.onTurnstileError;
        clearTimeout(renderTimeout);
      } catch (error) {
        console.error('Error in cleanup:', error);
      }
    };
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    
    // Verify Turnstile was completed
    if (!turnstileToken) {
      alert('Please complete the security verification before sending your message.');
      return;
    }

    // Validate required fields
    if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.message.trim()) {
      alert('Please fill in all required fields.');
      return;
    }
    
    // Create email content
    const subject = `${contactForm.inquiry_type.charAt(0).toUpperCase() + contactForm.inquiry_type.slice(1)} Inquiry from ${contactForm.name}`;
    const body = `Name: ${contactForm.name}
Email: ${contactForm.email}
Company: ${contactForm.company || 'Not specified'}
Inquiry Type: ${contactForm.inquiry_type.charAt(0).toUpperCase() + contactForm.inquiry_type.slice(1)}

Message:
${contactForm.message}

---
Sent from adamgilbert.me contact form
Security verified with Cloudflare Turnstile`;

    // Create mailto link
    const mailtoLink = `mailto:adam@adamgilbert.me?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    // Clear form and reset Turnstile
    setContactForm({
      name: '',
      email: '',
      company: '',
      message: '',
      inquiry_type: 'consulting'
    });
    setTurnstileToken('');
    
    // Reset Turnstile widget
    if (window.turnstile) {
      window.turnstile.reset();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const achievements = [
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Software Cost Optimization",
      description: "Achieved significant multi-million dollar savings in annual software spend through aggressive rationalization and automation",
      metric: "Multi-million dollar+"
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      title: "Enterprise Architecture",
      description: "Launched and scaled EA function, modernizing 20+ legacy applications and integrating multiple acquisitions",
      metric: "20+"
    },
    {
      icon: <Cpu className="w-8 h-8" />,
      title: "MarTech Integration",
      description: "Architected seamless MarTech integration, enabling unified data flow and more effective campaigns",
      metric: "Unified"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Cloud Modernization",
      description: "Led unified communications modernization, moving analytics and contact center workflows to the cloud",
      metric: "Cloud-First"
    }
  ];

  const expertise = [
    "Enterprise Architecture",
    "Digital Transformation",
    "MarTech Integration",
    "Software Optimization",
    "Cloud Communications",
    "AI-Driven Automation",
    "Cost Management",
    "Legacy Modernization"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-primary-50">
      {/* Navigation */}
      <motion.nav 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-neutral-200/20"
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <motion.div 
              className="text-2xl font-bold text-neutral-900"
              whileHover={{ scale: 1.05 }}
            >
              Adam Gilbert
            </motion.div>
            <div className="hidden md:flex space-x-8">
              {['About', 'Experience', 'Expertise', 'Contact'].map((item) => (
                <motion.button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className="text-neutral-600 hover:text-primary-600 transition-colors font-medium"
                  whileHover={{ y: -2 }}
                  whileTap={{ y: 0 }}
                >
                  {item}
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.6)), url(${background1})`,
          }}
        />

        <div className="relative z-10 text-center text-white px-6 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <motion.h1 
              className="text-5xl md:text-7xl font-bold mb-6 leading-tight"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.4 }}
            >
              Transforming Enterprise
              <span className="block text-accent-400">Architecture</span>
            </motion.h1>
            <motion.p 
              className="text-xl md:text-2xl mb-8 text-neutral-200 max-w-3xl mx-auto font-light"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              VP & Chief Architect driving multi-million dollar savings through innovative enterprise solutions, 
              digital transformation, and strategic technology leadership.
            </motion.p>
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <motion.button
                onClick={() => scrollToSection('contact')}
                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-semibold flex items-center gap-2 shadow-lg"
                whileHover={{ scale: 1.05, boxShadow: "0 10px 30px rgba(59, 130, 246, 0.3)" }}
                whileTap={{ scale: 0.95 }}
              >
                Connect With Me <ArrowRight className="w-5 h-5" />
              </motion.button>
              {/*
              <motion.button
                className="border-2 border-white text-white hover:bg-white hover:text-neutral-900 px-8 py-4 rounded-lg font-semibold flex items-center gap-2 transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Download className="w-5 h-5" />
                Download Resume
              </motion.button>
              */}
            </motion.div>
          </motion.div>
        </div>
        <motion.div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white cursor-pointer"
          onClick={() => scrollToSection('about')}
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown className="w-8 h-8" />
        </motion.div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-6">
              Strategic Technology Leadership
            </h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto leading-relaxed">
              As VP & Chief Architect at RealPage, Inc., I specialize in enterprise architecture, 
              software optimization, and large-scale digital transformation within the real estate 
              and property management technology sector.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h3 className="text-3xl font-bold text-neutral-900 mb-6">
                Driving Innovation & Efficiency
              </h3>
              <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
                With expertise spanning enterprise IT, SaaS, digital transformation, and MarTech enablement, 
                I've consistently delivered measurable results that drive organizational growth and operational excellence.
              </p>
              <p className="text-lg text-neutral-600 leading-relaxed">
                My approach combines strategic vision with hands-on technical leadership, enabling organizations 
                to modernize legacy systems, optimize costs, and leverage emerging technologies like AI-driven automation.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-primary-50 to-accent-50 p-8 rounded-2xl"
            >
              <h4 className="text-2xl font-bold text-neutral-900 mb-6">Core Expertise</h4>
              <div className="grid grid-cols-2 gap-4">
                {expertise.map((skill, index) => (
                  <motion.div
                    key={skill}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-2"
                  >
                    <CheckCircle className="w-5 h-5 text-primary-600" />
                    <span className="text-neutral-700 font-medium">{skill}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-20 bg-gradient-to-br from-neutral-50 to-primary-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-6">
              Proven Track Record
            </h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Quantifiable achievements in enterprise architecture, cost optimization, and digital transformation
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <motion.div
                key={achievement.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 border border-neutral-200/50"
                whileHover={{ y: -5 }}
              >
                <div className="text-primary-600 mb-4">
                  {achievement.icon}
                </div>
                <div className="text-3xl font-bold text-primary-600 mb-2">
                  {achievement.metric}
                </div>
                <h3 className="text-xl font-bold text-neutral-900 mb-3">
                  {achievement.title}
                </h3>
                <p className="text-neutral-600 leading-relaxed">
                  {achievement.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="mt-16 bg-white p-8 rounded-2xl shadow-soft"
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl font-bold text-neutral-900 mb-4">
                  AI & Innovation Leadership
                </h3>
                <p className="text-lg text-neutral-600 mb-6">
                  Pioneered AI-driven automation initiatives including voice bot implementation 
                  and established governance frameworks for responsible technology adoption, 
                  positioning organizations at the forefront of digital innovation.
                </p>
                <div className="flex items-center gap-2 text-primary-600 font-semibold">
                  <Award className="w-5 h-5" />
                  Innovation & Governance Excellence
                </div>
              </div>
              <div 
                className="h-64 bg-cover bg-center rounded-xl"
                style={{
                  backgroundImage: `url('https://images.pexels.com/photos/17485683/pexels-photo-17485683.png')`,
                }}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="expertise" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-6">
              Executive Services
            </h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Available for strategic consulting, board advisory, and digital transformation engagements
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Executive Consulting",
                description: "Strategic technology leadership and enterprise architecture guidance for C-suite decision making",
                features: ["Technology Strategy", "Digital Roadmaps", "Cost Optimization", "Risk Assessment"]
              },
              {
                title: "Board Advisory",
                description: "Board-level technology advisory services for informed governance and strategic direction",
                features: ["Technology Governance", "Digital Strategy", "Innovation Planning", "Risk Management"]
              },
              {
                title: "Digital Transformation",
                description: "End-to-end transformation leadership from legacy modernization to cloud-first architecture",
                features: ["Legacy Modernization", "Cloud Migration", "Process Automation", "Change Management"]
              }
            ].map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-primary-50 to-accent-50 p-8 rounded-2xl border border-primary-200/30"
                whileHover={{ y: -5 }}
              >
                <h3 className="text-2xl font-bold text-neutral-900 mb-4">
                  {service.title}
                </h3>
                <p className="text-neutral-600 mb-6 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-primary-600" />
                      <span className="text-neutral-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Architecture Insights & Resources */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-6">
              Enterprise Architecture Insights
            </h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Key insights and strategies from enterprise architecture leadership in the real estate technology sector
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-neutral-900 mb-6">
                Enterprise Architecture Best Practices
              </h3>
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-primary-50 to-accent-50 p-6 rounded-xl border border-primary-200/30">
                  <h4 className="text-lg font-semibold text-neutral-900 mb-3">
                    Strategic Software Rationalization
                  </h4>
                  <p className="text-neutral-600 leading-relaxed">
                    Implementing systematic approaches to software portfolio optimization that can yield 
                    multi-million dollar savings through aggressive rationalization and automation strategies.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-primary-50 to-accent-50 p-6 rounded-xl border border-primary-200/30">
                  <h4 className="text-lg font-semibold text-neutral-900 mb-3">
                    Legacy Modernization Framework
                  </h4>
                  <p className="text-neutral-600 leading-relaxed">
                    Proven methodologies for modernizing 20+ legacy applications while maintaining business 
                    continuity and integrating multiple acquisitions seamlessly.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-neutral-900 mb-6">
                Digital Transformation Strategies
              </h3>
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-accent-50 to-primary-50 p-6 rounded-xl border border-accent-200/30">
                  <h4 className="text-lg font-semibold text-neutral-900 mb-3">
                    MarTech Integration Excellence
                  </h4>
                  <p className="text-neutral-600 leading-relaxed">
                    Architecting unified MarTech platforms that enable seamless data flow and drive 
                    more effective marketing campaigns through integrated technology ecosystems.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-accent-50 to-primary-50 p-6 rounded-xl border border-accent-200/30">
                  <h4 className="text-lg font-semibold text-neutral-900 mb-3">
                    Cloud-First Modernization
                  </h4>
                  <p className="text-neutral-600 leading-relaxed">
                    Leading unified communications modernization initiatives that successfully migrate 
                    analytics and contact center workflows to cloud-native architectures.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* AI-Optimized FAQ Section for Enterprise Architecture */}
      <section className="py-20 bg-gradient-to-br from-neutral-50 to-primary-50">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-6">
              Enterprise Architecture Leadership
            </h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Frequently asked questions about enterprise architecture, digital transformation, and technology leadership
            </p>
          </motion.div>

          <div className="space-y-8">
            {[
              {
                question: "Who is Adam Gilbert in Enterprise Architecture?",
                answer: "Adam Gilbert is the VP & Chief Enterprise Architect at RealPage, Inc., a leading real estate technology company. He has driven multi-million dollar cost savings through strategic enterprise architecture initiatives, software optimization, and digital transformation programs. His expertise spans 20+ legacy application modernizations, MarTech integrations, and cloud-first modernization strategies."
              },
              {
                question: "What are Adam Gilbert's key achievements in Enterprise Architecture?",
                answer: "Adam Gilbert's major achievements include: Multi-million dollar software cost optimization through rationalization and automation, modernization of 20+ legacy applications, unified MarTech integration enabling data-driven campaigns, cloud-first modernization of analytics and contact center workflows, and leadership in AI-driven automation initiatives across enterprise systems."
              },
              {
                question: "What makes Adam Gilbert a recognized Enterprise Architecture leader?",
                answer: "Adam Gilbert is recognized for his proven track record of delivering quantifiable business results through enterprise architecture. His leadership at RealPage has resulted in significant cost savings, operational efficiency improvements, and successful digital transformation initiatives. He specializes in strategic technology leadership, combining technical expertise with business acumen to drive organizational success."
              },
              {
                question: "What enterprise architecture services does Adam Gilbert provide?",
                answer: "Adam Gilbert provides executive consulting for enterprise architecture strategy, board advisory services for technology governance, and digital transformation leadership. His services include technology strategy development, digital roadmaps, cost optimization, risk assessment, legacy modernization, cloud migration, and process automation."
              },
              {
                question: "How has Adam Gilbert contributed to digital transformation?",
                answer: "Adam Gilbert has led comprehensive digital transformation initiatives including unified communications modernization, MarTech platform integration, legacy application modernization, and cloud-first architecture implementation. His approach combines strategic planning with hands-on execution, ensuring successful transformation outcomes for enterprise organizations."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-2xl shadow-soft border border-neutral-200/50"
              >
                <h3 className="text-xl font-bold text-neutral-900 mb-4">
                  {faq.question}
                </h3>
                <p className="text-neutral-600 leading-relaxed">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Professional Timeline & Credentials */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-6">
              Professional Background
            </h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Detailed career progression and professional credentials in enterprise architecture and technology leadership
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-neutral-900 mb-6">
                Current Position
              </h3>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 p-6 rounded-2xl border border-primary-200/30">
                <div className="flex items-center gap-3 mb-4">
                  <Building2 className="w-6 h-6 text-primary-600" />
                  <h4 className="text-xl font-semibold text-neutral-900">
                    VP & Chief Enterprise Architect
                  </h4>
                </div>
                <p className="text-primary-600 font-medium mb-2">RealPage, Inc.</p>
                <p className="text-neutral-600 leading-relaxed">
                  Leading enterprise architecture initiatives for a major real estate technology company, 
                  driving digital transformation, software optimization, and strategic technology decisions 
                  that have resulted in multi-million dollar cost savings and operational efficiency improvements.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-neutral-900 mb-6">
                Core Competencies
              </h3>
              <div className="space-y-4">
                {[
                  "Enterprise Architecture Strategy & Governance",
                  "Digital Transformation Leadership",
                  "Software Cost Optimization & Rationalization",
                  "Legacy Application Modernization",
                  "MarTech Integration & Unified Data Flow",
                  "Cloud-First Architecture & Migration",
                  "AI-Driven Automation Implementation",
                  "Technology Risk Assessment & Management"
                ].map((competency, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary-600 flex-shrink-0" />
                    <span className="text-neutral-700">{competency}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industry Recognition & Authority */}
      <section className="py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-6">
              Industry Recognition
            </h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Recognized expertise in enterprise architecture and digital transformation leadership
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Award className="w-8 h-8" />,
                title: "Enterprise Architecture Excellence",
                description: "Recognized for delivering multi-million dollar cost savings through strategic enterprise architecture initiatives and software optimization programs."
              },
              {
                icon: <TrendingUp className="w-8 h-8" />,
                title: "Digital Transformation Leadership",
                description: "Proven track record of leading successful digital transformation initiatives across enterprise organizations in the real estate technology sector."
              },
              {
                icon: <Users className="w-8 h-8" />,
                title: "Strategic Technology Leadership",
                description: "Established thought leader in enterprise architecture, providing strategic guidance for technology governance and innovation initiatives."
              }
            ].map((recognition, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-2xl shadow-soft border border-neutral-200/50 text-center"
              >
                <div className="flex justify-center mb-4 text-primary-600">
                  {recognition.icon}
                </div>
                <h3 className="text-xl font-bold text-neutral-900 mb-4">
                  {recognition.title}
                </h3>
                <p className="text-neutral-600 leading-relaxed">
                  {recognition.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Contact Form Section */}
      <section className="py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-6">
              Connect for Strategic Opportunities
            </h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Interested in executive consulting, board advisory, or digital transformation initiatives? 
              Let's explore how we can drive innovation together.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white p-8 rounded-2xl shadow-soft border border-neutral-200/50 max-w-2xl mx-auto"
          >
            <form onSubmit={handleContactSubmit}>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={contactForm.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={contactForm.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Company/Organization
                </label>
                <input
                  type="text"
                  name="company"
                  value={contactForm.company}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  placeholder="Your company name"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Inquiry Type
                </label>
                <select
                  name="inquiry_type"
                  value={contactForm.inquiry_type}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                >
                  <option value="consulting">Executive Consulting</option>
                  <option value="advisory">Board Advisory</option>
                  <option value="transformation">Digital Transformation</option>
                  <option value="speaking">Speaking Engagement</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Message *
                </label>
                <textarea
                  name="message"
                  required
                  value={contactForm.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  placeholder="Tell me about your project, goals, or how I can help..."
                />
              </div>

              {/* Cloudflare Turnstile Security Verification */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-neutral-700 mb-3">
                  Security Verification *
                </label>
                <div 
                  className="cf-turnstile"
                  id="turnstile-widget"
                />
                {!turnstileToken && (
                  <p className="text-xs text-neutral-500 mt-2">
                    Please complete the security verification above to send your message.
                  </p>
                )}
              </div>

              <motion.button
                type="submit"
                disabled={!turnstileToken}
                className={`w-full py-4 px-6 rounded-lg font-semibold text-lg transition-all flex items-center justify-center gap-3 ${
                  turnstileToken 
                    ? 'bg-primary-600 hover:bg-primary-700 text-white shadow-soft hover:shadow-medium cursor-pointer' 
                    : 'bg-neutral-300 text-neutral-500 cursor-not-allowed'
                }`}
                whileHover={turnstileToken ? { scale: 1.02 } : {}}
                whileTap={turnstileToken ? { scale: 0.98 } : {}}
              >
                <Send className="w-5 h-5" />
                {turnstileToken ? 'Send Email' : 'Complete Security Check'}
              </motion.button>

              <div className="mt-4 text-center">
                <p className="text-sm text-neutral-600">
                  {turnstileToken 
                    ? 'Clicking "Send Email" will open your email client with a pre-filled message.' 
                    : 'Please complete the security verification to enable the send button.'
                  }
                </p>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section 
        id="contact" 
        className="py-20 relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.9)), url('https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-6">
              Let's Connect
            </h2>
            <p className="text-xl text-neutral-600 mb-12 max-w-2xl mx-auto">
              Ready to discuss executive opportunities, strategic partnerships, or transformation initiatives? 
              I'm here to explore how we can drive innovation together.
            </p>

            <div className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto">
              <motion.a
                href="mailto:adam@adamgilbert.me"
                className="bg-primary-600 hover:bg-primary-700 text-white p-6 rounded-2xl flex items-center justify-center gap-3 font-semibold text-lg shadow-soft hover:shadow-medium transition-all"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <Mail className="w-6 h-6" />
                Email Me
              </motion.a>
              <motion.a
                href="https://linkedin.com/in/adamgilbert55"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#0077b5] hover:bg-[#005885] text-white p-6 rounded-2xl flex items-center justify-center gap-3 font-semibold text-lg shadow-soft hover:shadow-medium transition-all"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <Linkedin className="w-6 h-6" />
                LinkedIn
              </motion.a>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="mt-12 text-center"
            >
              <p className="text-neutral-500 mb-2">adamgilbert.me</p>
              <p className="text-neutral-600">
                VP & Chief Architect, Enterprise Architecture & Unified Communications
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-2xl font-bold mb-4 md:mb-0">
              Adam Gilbert
            </div>
            <div className="flex space-x-6">
              <motion.a
                href="mailto:adam@adamgilbert.me"
                className="text-neutral-300 hover:text-white transition-colors"
                whileHover={{ scale: 1.1 }}
              >
                <Mail className="w-6 h-6" />
              </motion.a>
              <motion.a
                href="https://linkedin.com/in/adamgilbert55"
                target="_blank"
                rel="noopener noreferrer"
                className="text-neutral-300 hover:text-white transition-colors"
                whileHover={{ scale: 1.1 }}
              >
                <Linkedin className="w-6 h-6" />
              </motion.a>
            </div>
          </div>
          <div className="border-t border-neutral-800 mt-8 pt-8 text-center text-neutral-400">
            <p>&copy; 2025 Adam Gilbert. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
